// ═══════════════════════════════════════════════════════════
// Incident helpers — shared type metadata, icons, colors
// ═══════════════════════════════════════════════════════════

import type { ComponentType } from 'react';
import {
  IconCloudStorm, IconFlame, IconBiohazard, IconClipboardCheck,
  IconSchool, IconInfoCircle, IconDroplet, IconBuildingBridge,
  IconTornado, IconCalendar, IconShield, IconAmbulance, IconConfetti,
} from '@tabler/icons-react';
import type { BadgeVariant } from './incident-types';

export const INCIDENT_TYPE_META: Record<string, { label: string; color: string; icon: string }> = {
  natural_disaster: { label: 'Natural Disaster', color: '#0EA5E9', icon: 'IconCloudStorm' },
  mci:             { label: 'Mass Casualty',     color: 'var(--color-error)', icon: 'IconAmbulance' },
  hazmat:          { label: 'Hazmat',            color: '#A67103', icon: 'IconBiohazard' },
  exercise:        { label: 'Exercise',          color: '#A627FF', icon: 'IconClipboardCheck' },
  training:        { label: 'Training',          color: '#07963F', icon: 'IconSchool' },
  special_event:   { label: 'Special Event',     color: '#E85D04', icon: 'IconConfetti' },
  other:           { label: 'Other',             color: '#7B7D7F', icon: 'IconInfoCircle' },
  // Legacy types
  hurricane:       { label: 'Hurricane',         color: '#0EA5E9', icon: 'IconCloudStorm' },
  wildfire:        { label: 'Wildfire',          color: '#D97706', icon: 'IconFlame' },
  flood:           { label: 'Flood',             color: '#0EA5E9', icon: 'IconDroplet' },
  earthquake:      { label: 'Earthquake',        color: 'var(--color-error)', icon: 'IconBuildingBridge' },
  tornado:         { label: 'Tornado',           color: '#6366F1', icon: 'IconTornado' },
  winter_storm:    { label: 'Winter Storm',      color: '#0EA5E9', icon: 'IconCloudStorm' },
  pandemic:        { label: 'Pandemic',          color: 'var(--color-error)', icon: 'IconAmbulance' },
  civil_unrest:    { label: 'Civil Unrest',      color: 'var(--color-error)', icon: 'IconShield' },
  planned:         { label: 'Planned',           color: 'var(--color-primary-700)', icon: 'IconCalendar' },
  general:         { label: 'General',           color: '#64748B', icon: 'IconShield' },
};

const ICON_MAP: Record<string, ComponentType<{ size?: number; color?: string }>> = {
  IconCloudStorm, IconFlame, IconBiohazard, IconClipboardCheck,
  IconSchool, IconInfoCircle, IconDroplet, IconBuildingBridge,
  IconTornado, IconCalendar, IconShield, IconAmbulance, IconConfetti,
};

export function typeIcon(type: string): ComponentType<{ size?: number; color?: string }> {
  const meta = INCIDENT_TYPE_META[type] || INCIDENT_TYPE_META.other;
  return ICON_MAP[meta.icon] || IconInfoCircle;
}

export function statusColor(status: string): BadgeVariant {
  if (status === 'active') return 'warning';
  if (status === 'monitoring') return 'primary';
  if (status === 'closed') return 'success';
  return 'neutral';
}

export const TYPE_OPTIONS = [
  { value: 'natural_disaster', label: 'Natural Disaster' },
  { value: 'hurricane', label: 'Hurricane' },
  { value: 'tornado', label: 'Tornado' },
  { value: 'flood', label: 'Flood' },
  { value: 'wildfire', label: 'Wildfire' },
  { value: 'earthquake', label: 'Earthquake' },
  { value: 'winter_storm', label: 'Winter Storm' },
  { value: 'mci', label: 'Mass Casualty Incident' },
  { value: 'hazmat', label: 'Hazardous Materials' },
  { value: 'pandemic', label: 'Pandemic' },
  { value: 'civil_unrest', label: 'Civil Unrest' },
  { value: 'exercise', label: 'Exercise / Drill' },
  { value: 'training', label: 'Training' },
  { value: 'special_event', label: 'Special Event' },
  { value: 'other', label: 'Other' },
];

export const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' },
];

export const SEVERITY_OPTIONS = [
  { value: '', label: 'Select severity...' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];
